import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import "./ChatBot.css";

interface FraudControlModalStrings {
  title: string;
  description: string;
  upload: string;
  close: string;
}

interface ChatInputProps {
  readonly onSendMessage: (message: string) => void;
  readonly onUploadCsv?: (file: File) => void;
  readonly disabled?: boolean;
  readonly uploadDisabled?: boolean;
  readonly placeholder?: string;
  readonly sendLabel?: string;
  readonly uploadButtonTitle?: string;
  readonly fraudControlModal?: FraudControlModalStrings;
}

export function ChatInput({
  onSendMessage,
  onUploadCsv,
  disabled,
  uploadDisabled,
  placeholder,
  sendLabel = "Send",
  uploadButtonTitle = "Upload CSV for fraud control",
  fraudControlModal,
}: ChatInputProps) {
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent | Event) => {
      if (disabled) return;

      const keyEvent = e as KeyboardEvent;
      const key = keyEvent.key;

      if (
        key.length === 1 &&
        !keyEvent.ctrlKey &&
        !keyEvent.metaKey &&
        !keyEvent.altKey
      ) {
        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.getAttribute("contenteditable") === "true");

        if (!isInputFocused && inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown as EventListener);
    return () => {
      document.removeEventListener("keydown", handleKeyDown as EventListener);
    };
  }, [disabled]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleUploadButtonClick = () => {
    setPendingFile(null);
    setShowModal(true);
  };

  const handleModalFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setPendingFile(file);
  };

  const handleModalUpload = () => {
    if (pendingFile && onUploadCsv) {
      setShowModal(false);
      onUploadCsv(pendingFile);
    }
    if (modalFileInputRef.current) {
      modalFileInputRef.current.value = "";
    }
    setPendingFile(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setPendingFile(null);
    if (modalFileInputRef.current) {
      modalFileInputRef.current.value = "";
    }
  };

  const modal = fraudControlModal ?? {
    title: "Fraud Control",
    description:
      "Upload a CSV file with three columns: MSISDN, JMBG, and Sign Date. The system will return a downloadable list of entries not found in Aurora.",
    upload: "Upload",
    close: "Close",
  };

  return (
    <div className="chat-input-container">
      {onUploadCsv && (
        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>{modal.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{modal.description}</p>
            <Form.Control
              ref={modalFileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleModalFileChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              {modal.close}
            </Button>
            <Button
              variant="primary"
              onClick={handleModalUpload}
              disabled={!pendingFile}
            >
              {modal.upload}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <InputGroup>
        {onUploadCsv && (
          <Button
            variant="outline-secondary"
            onClick={handleUploadButtonClick}
            disabled={disabled || uploadDisabled}
            title={uploadButtonTitle}
            className="upload-csv-button"
          >
            📎
          </Button>
        )}
        <Form.Control
          ref={inputRef}
          type="text"
          placeholder={placeholder || "Type your message..."}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="chat-input"
        />
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          className="send-button"
        >
          {sendLabel}
        </Button>
      </InputGroup>
    </div>
  );
}
